import BreadCrumbs from 'components/breadcrumbs'
import LoadingV2 from 'components/LoadingV2'
import PMBModalRemove from 'components/PMBModalRemove'
import PMBSearchInputGroup from 'components/PMBSearchInputGroup'
import PMBSelect from 'components/PMBSelect'
import PMBTooltip from 'components/PMBTooltip'
import { TableComponent as DataTable } from 'components/TableComponent'
import moment from 'moment-timezone'
import { useSnackbar } from 'notistack'
import { useEffect, useMemo, useState } from 'react'
import { Download, Edit2, Eye, Plus, Trash2 } from 'react-feather'
import { useNavigate } from 'react-router-dom'
import { Button, Card, CardBody, Spinner } from 'reactstrap'
import { formatInputNumber } from 'utility/Utils'
import { URL_REPORT_PNBP_DETAIL, URL_WORKPLACE } from 'utils/pathUrl'
import { v4 as uuid } from 'uuid'
import ModalInputDownload from './ModalInputDownload'
import ModalSetTilte from './ModalSetTilte'
import {
  useAddReportMutation,
  useFetchReportsQuery,
} from './reportPnbpListApiSlice'
import LihatDetail from 'components/Icons/LihatDetail'

const ListReportPnbp = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [addReport, { isLoading: isLoadingAddReport }] = useAddReportMutation()

  const {
    data: reports,
    isLoading,
    isFetching,
    refetch: refetchAddReport,
  } = useFetchReportsQuery()

  const [editDataItemRemove, setEditDataItemRemove] = useState(null)
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [periodeFilter, setPeriodeFilter] = useState('all')
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [dataReportById, setDataReportById] = useState(null)
  const isEditMode = Boolean(dataReportById)

  const [isOpenModalSetTitle, setIsOpenModalSetTitle] = useState(false)
  const toggleModalSetTitle = () => {
    setIsOpenModalSetTitle((prev) => !prev)
    setDataReportById(null)
  }

  const [isOpenModalInputDownload, setIsOpenModalInputDownload] =
    useState(false)
  const toggleModalInputDownload = () => {
    setIsOpenModalInputDownload((prev) => !prev)
    setDataReportById(null)
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const [payload, setPayload] = useState({
    id: null,
    title: `Rincian Pembayaran PNBP`,
    periode: moment().format('YYYY-MM'),
    status: 'Draft',
  })

  const filteredData = useMemo(() => {
    let result = reports || []

    // FILTER BASED ON PERIODE
    if (periodeFilter !== 'all') {
      result = result.filter((item) => item.periode === periodeFilter)
    }

    // FILTER BASED ON SEARCH
    if (searchInput) {
      const searchTerm = searchInput.toLowerCase()
      result = result.filter((item) => {
        const matchTitle = item.title?.toLowerCase().includes(searchTerm)
        const matchStatus = item.status?.toLowerCase().includes(searchTerm)

        const periodeMoment = moment(item?.periode, 'YYYY-MM')
        const matchPeriode =
          item?.periode?.includes(searchTerm) ||
          periodeMoment?.format('MMMM')?.toLowerCase()?.includes(searchTerm) ||
          periodeMoment
            ?.locale('id')
            ?.format('MMMM')
            ?.toLowerCase()
            ?.includes(searchTerm)

        return matchTitle || matchPeriode || matchStatus
      })
    }

    return result
  }, [searchInput, reports, periodeFilter])

  // RESET STATE
  const resetState = () => {
    setPayload({
      id: null,
      title: `Rincian Pembayaran PNBP`,
      periode: moment().format('YYYY-MM'),
      status: 'Draft',
    })
  }

  // HANDLE TITLE
  const handleTitleChange = (title) => {
    setPayload((prev) => ({
      ...prev,
      title,
    }))
  }

  // HANDLE PERIODE
  const handlePeriodeChange = (periode) => {
    setPayload((prev) => ({
      ...prev,
      periode,
    }))
  }

  // HANDLE SUBMIT
  const handleSubmit = async () => {
    try {
      if (isFormInvalid) {
        enqueueSnackbar('Lengkapi data terlebih dahulu', {
          variant: 'error',
        })
        return
      }

      let payloadArray = []

      // CREATE MODE
      const finalUuid = uuid().split('-').join('')
      const timestamp = Date.now()
      const uniqueId = finalUuid + timestamp

      if (isEditMode) {
        payloadArray = (reports || []).map((report) => {
          if (report.id === dataReportById?.id) {
            return { ...payload, id: dataReportById?.id }
          }
          return report
        })
      } else {
        payloadArray = [{ ...payload, id: uniqueId }, ...(reports || [])]
      }

      const response = await addReport(payloadArray)
      if (response && response?.data) {
        enqueueSnackbar('Laporan berhasil ditambahkan', {
          variant: 'success',
        })
        refetchAddReport()
        toggleModalSetTitle()
        resetState()
        if (!isEditMode) {
          navigate(`${URL_REPORT_PNBP_DETAIL}?id=${uniqueId}`)
        }
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Laporan gagal ditambahkan', {
        variant: 'error',
      })
    }
  }

  // HANDLE REMOVE BY REPORT ID
  const handleSubmitRemoveByReportId = async (reportId) => {
    try {
      // FILTER OUT THE REPORT WITH THE MATCHING ID
      const updatedReports = (reports || []).filter(
        (report) => report.id !== reportId,
      )

      const response = await addReport(updatedReports)
      if (response && response?.data) {
        enqueueSnackbar('Laporan berhasil dihapus', {
          variant: 'success',
        })
        refetchAddReport()
        setIsOpenDeleteModal(false)
        setEditDataItemRemove(null)
      }
    } catch (error) {
      enqueueSnackbar(error?.data?.message ?? 'Laporan gagal dihapus', {
        variant: 'error',
      })
    }
  }

  // COLUMNS
  const columns = [
    {
      name: <span>Judul</span>,
      selector: 'title',
      grow: 2,
      cell: (row) => {
        return <span>{row?.title ?? '-'}</span>
      },
      minWidth: '250px',
    },
    {
      name: <span>Periode</span>,
      selector: 'periode',
      grow: 2,
      left: true,
      cell: (row) => {
        return (
          <span>
            {row?.periode ? moment(row?.periode).format('MMMM Y') : '-'}
          </span>
        )
      },
      minWidth: '150px',
    },
    // {
    //   name: <span>Status</span>,
    //   selector: 'status',
    //   grow: 2,
    //   right: true,
    //   cell: (row) => {
    //     return <span>{row?.status ?? '-'}</span>
    //   },
    // },
    {
      name: <span>Total</span>,
      selector: 'total',
      grow: 2,
      right: true,
      cell: (row) => {
        const total = row?.itemConfigurations?.reduce((sum, item) => {
          const itemTotal = item?.total || 0
          return sum + itemTotal
        }, 0)
        return <span>Rp{formatInputNumber(total ?? 0)} </span>
      },
      minWidth: '150px',
    },
    {
      name: '',
      grow: 2,
      center: true,
      minWidth: '100px',
      cell: (row) => {
        return (
          <div className="d-flex justify-content-center">
            <LihatDetail
              onClick={() => {
                navigate(`${URL_REPORT_PNBP_DETAIL}?id=${row?.id}`)
              }}
              id={row?.id}
              className="mx-2 p-0"
              data-testid={`pnbp-row-detail-${row.id}`}
            />
            <div
              className="d-flex align-items-center"
              style={{ gap: '16px', flexGrow: 1 }}
            >
              <div id={`ledger-${row?.id}`}>
                <Download
                  size={18}
                  data-testid={`pnbp-download-button-${row?.id}`}
                  onClick={() => {
                    setDataReportById(row)
                    setIsOpenModalInputDownload(true)
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <PMBTooltip
                target={`ledger-${row?.id}`}
                text="Unduh Laporan Buku Besar dan Laporan Produksi Pembayaran"
              />

              <div id={`ubah-${row?.id}`}>
                <Edit2
                  size={18}
                  onClick={() => {
                    toggleModalSetTitle()
                    setDataReportById(row)
                    setPayload({
                      id: row?.id,
                      title: row?.title,
                      periode: row?.periode,
                      status: row?.status,
                      itemConfigurations: row?.itemConfigurations,
                    })
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <PMBTooltip
                target={`ubah-${row?.id}`}
                text="Ubah Judul atau Periode"
              />

              <div id={`remove-${row?.id}`}>
                <Trash2
                  color="red"
                  size={18}
                  onClick={() => {
                    setIsOpenDeleteModal(true)
                    setEditDataItemRemove(row)

                    setPayload({
                      id: row?.id,
                      title: row?.title,
                      periode: row?.periode,
                      status: row?.status,
                    })
                  }}
                  style={{ cursor: 'pointer' }}
                />
              </div>
              <PMBTooltip target={`remove-${row?.id}`} text="Hapus" />
            </div>
          </div>
        )
      },
    },
  ]

  const periodeOptions = useMemo(() => {
    const options = [{ value: 'all', label: 'Semua Periode' }]

    if (reports?.length > 0) {
      const uniquePeriods = [...new Set(reports.map((item) => item.periode))]
        ?.sort()
        ?.reverse()
        ?.map((periode) => ({
          value: periode,
          label: moment(periode).format('MMMM Y'),
        }))

      options.push(...uniquePeriods)
    }

    return options
  }, [reports])

  const isFilterActive = periodeFilter !== 'all' || !!searchInput

  // HANDLE IS FORM INVALID
  const isFormInvalid = !payload.title || !payload.periode

  if (isLoading) {
    return <LoadingV2 />
  }

  return (
    <div className="w-100 px-3">
      {/* MODAL INPUT DOWNLOAD */}
      <ModalInputDownload
        isOpen={isOpenModalInputDownload}
        toggle={toggleModalInputDownload}
        dataReportById={dataReportById}
      />

      {/* MODAL DELETE */}
      <PMBModalRemove
        isOpen={isOpenDeleteModal}
        onClose={() => setIsOpenDeleteModal((prev) => !prev)}
        title={`Hapus Laporan`}
        dataId={editDataItemRemove?.id}
        desc={`Apakah Anda yakin ingin menghapus ${editDataItemRemove?.title}?`}
        isLoading={isLoadingAddReport}
        onRemoveData={(id) => handleSubmitRemoveByReportId(id)}
        buttonTitle="Hapus"
      />

      {/* MODAL SET TITLE */}
      <ModalSetTilte
        isOpen={isOpenModalSetTitle}
        toggle={toggleModalSetTitle}
        payload={payload}
        handleTitleChange={handleTitleChange}
        handlePeriodeChange={handlePeriodeChange}
        resetState={resetState}
        isFormInvalid={isFormInvalid}
        handleSubmit={handleSubmit}
        isLoadingAddReport={isLoadingAddReport}
        isEditMode={isEditMode}
        isListReport={true}
      />

      <div className="mt-3">
        <BreadCrumbs
          breadCrumbActive="Daftar Laporan PNBP"
          breadCrumbParent={'Alat Bantu'}
          breadCrumbParentLink={URL_WORKPLACE}
        />
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <CardBody>
          <DataTable
            columns={columns}
            data={filteredData || []}
            progressPending={isFetching}
            progressComponent={
              <div style={{ margin: '100px 0px' }}>
                <Spinner color="primary" />
              </div>
            }
            subHeader={true}
            subHeaderComponent={
              <div className="w-100">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <h5 className="fw-semibold">Daftar Laporan PNBP</h5>

                  <Button
                    onClick={() => toggleModalSetTitle()}
                    size="sm"
                    color="primary"
                    data-testid="pnbp-add-item-button"
                  >
                    <Plus size={16} /> Tambah
                  </Button>
                </div>

                <div
                  className="my-4 d-flex align-items-center"
                  style={{
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: '1rem',
                  }}
                >
                  <div style={{ width: isMobile ? '100%' : '220px' }}>
                    <PMBSearchInputGroup
                      label={'Cari'}
                      value={searchInput}
                      placeholder="Cari laporan..."
                      onChange={(e) => setSearchInput(e.target.value)}
                      maxWidth={isMobile ? '100%' : '100%'}
                    />
                  </div>

                  <div style={{ width: isMobile ? '100%' : '220px' }}>
                    <PMBSelect
                      title="Periode"
                      options={periodeOptions}
                      value={periodeOptions.find(
                        (opt) => opt.value === periodeFilter,
                      )}
                      onChange={(e) => setPeriodeFilter(e.value)}
                      style={{ width: '100%' }}
                    />
                  </div>
                </div>
              </div>
            }
            noDataComponent={
              <p className="my-3 fw-semibold">
                {isFilterActive
                  ? 'Tidak ada data yang sesuai dengan pencarian Anda'
                  : 'Belum ada daftar laporan pnbp yang tersedia'}
              </p>
            }
          />
        </CardBody>
      </Card>
    </div>
  )
}

export default ListReportPnbp
